import { queryClient } from "../query-client";
import { queryKeys } from "../query-keys";
import { adminApi } from "../api";
import { STALE_2MIN, STALE_5MIN } from "./shared";

export function prefetchAdminDashboard() {
	queryClient.ensureQueryData({
		queryKey: queryKeys.admin.dashboard(),
		queryFn: async () => {
			const [
				prosRes,
				publishedProsRes,
				projectsRes,
				publishedProjectsRes,
			] = await Promise.all([
				adminApi.listPros({ limit: "5" }),
				adminApi.listPros({ limit: "1", status: "published" }),
				adminApi.listProjects({ limit: "1" }),
				adminApi.listProjects({ limit: "1", status: "published" }),
			]);
			return {
				stats: {
					totalPros: prosRes.meta?.total ?? 0,
					publishedPros: publishedProsRes.meta?.total ?? 0,
					totalProjects: projectsRes.meta?.total ?? 0,
					publishedProjects: publishedProjectsRes.meta?.total ?? 0,
				},
				recentPros: (prosRes.data || []).slice(0, 5),
			};
		},
		staleTime: STALE_2MIN,
	});
}

export function prefetchAdminPros() {
	queryClient.prefetchInfiniteQuery({
		queryKey: queryKeys.admin.pros.list({}),
		queryFn: ({ pageParam = 0 }) =>
			adminApi.listPros({ limit: "20", offset: pageParam.toString() }),
		initialPageParam: 0,
		staleTime: STALE_5MIN,
	});
}

export function prefetchAdminProDetail(proId: string) {
	queryClient.ensureQueryData({
		queryKey: queryKeys.admin.pros.detail(proId),
		queryFn: () => adminApi.getPro(proId).then((r) => r.data),
		staleTime: STALE_5MIN,
	});
}

export function prefetchAdminProAnalytics(proId: string) {
	queryClient.ensureQueryData({
		queryKey: queryKeys.admin.pros.detail(proId),
		queryFn: () => adminApi.getPro(proId).then((r) => r.data),
		staleTime: STALE_5MIN,
	});
	queryClient.ensureQueryData({
		queryKey: queryKeys.admin.pros.stats(proId),
		queryFn: () => adminApi.getProStats(proId).then((r) => r.data),
		staleTime: STALE_2MIN,
	});
}

export function prefetchAdminProCompanyProfile(proId: string) {
	queryClient.ensureQueryData({
		queryKey: queryKeys.admin.pros.companyProfile(proId),
		queryFn: () =>
			adminApi.getCompanyProfile(proId).then((r) => r.data),
		staleTime: STALE_5MIN,
	});
	queryClient.ensureQueryData({
		queryKey: queryKeys.admin.pros.detail(proId),
		queryFn: () => adminApi.getPro(proId).then((r) => r.data),
		staleTime: STALE_5MIN,
	});
}

export function prefetchAdminProjects() {
	queryClient.prefetchInfiniteQuery({
		queryKey: queryKeys.admin.projects.list({}),
		queryFn: ({ pageParam = 0 }) =>
			adminApi.listProjects({ limit: "20", offset: pageParam.toString() }),
		initialPageParam: 0,
		staleTime: STALE_5MIN,
	});
}

export function prefetchAdminUsers() {
	queryClient.prefetchInfiniteQuery({
		queryKey: queryKeys.admin.users.list({}),
		queryFn: ({ pageParam = 0 }) =>
			adminApi.listUsers({ limit: "20", offset: pageParam.toString() }),
		initialPageParam: 0,
		staleTime: STALE_5MIN,
	});
}

export function prefetchAdminUserDetail(userId: string) {
	queryClient.ensureQueryData({
		queryKey: queryKeys.admin.users.detail(userId),
		queryFn: () => adminApi.getUser(userId).then((r) => r.data),
		staleTime: STALE_5MIN,
	});
}

// Admin taxonomy types used for prefetching — must match the camelCase format
// expected by the API (see apps/api/src/routes/admin/taxonomy.config.ts).
// Exported for test validation against the API contract.
export const ALL_ADMIN_TAXONOMY_TYPES = ["businessTypes"] as const;

export function prefetchAdminTaxonomy() {
	queryClient.ensureQueryData({
		queryKey: queryKeys.admin.taxonomy("businessTypes"),
		queryFn: () =>
			adminApi.listTaxonomy("businessTypes", false).then((r) => r.data || []),
		staleTime: STALE_5MIN,
	});
}

export function prefetchAdminBlogs() {
	queryClient.ensureQueryData({
		queryKey: queryKeys.admin.blogs.list({}),
		queryFn: () => adminApi.listBlogs().then((r) => r.data || []),
		staleTime: STALE_5MIN,
	});
}

export function prefetchAdminBlogEdit(blogId: string) {
	queryClient.ensureQueryData({
		queryKey: queryKeys.admin.blogs.detail(blogId),
		queryFn: () => adminApi.getBlog(blogId).then((r) => r.data),
		staleTime: STALE_5MIN,
	});
	queryClient.ensureQueryData({
		queryKey: queryKeys.admin.blogs.categories(),
		queryFn: () =>
			adminApi.listBlogCategories(true).then((r) => r.data || []),
		staleTime: STALE_5MIN,
	});
}

export function prefetchAdminBlogCategories() {
	queryClient.ensureQueryData({
		queryKey: queryKeys.admin.blogs.categories(),
		queryFn: () =>
			adminApi.listBlogCategories(true).then((r) => r.data || []),
		staleTime: STALE_5MIN,
	});
}

export function prefetchAdminBlogSuggestions() {
	queryClient.ensureQueryData({
		queryKey: queryKeys.admin.blogs.suggestions({}),
		queryFn: () =>
			adminApi.listBlogSuggestions().then((r) => r.data || []),
		staleTime: STALE_5MIN,
	});
}
